<template>
  <div
    :role="isError ? 'alert' : 'status'"
    :aria-live="isError ? 'assertive' : 'polite'"
    class="w-bulk-import-toast rounded-2xl border border-grey-100 bg-white px-3.5 py-4 flex flex-col shadow-bulk-import-toast"
    :class="isPreparing ? 'gap-4' : 'gap-5'"
  >
    <div class="flex items-center gap-3">
      <div
        class="flex shrink-0 items-center justify-center w-8 h-8 rounded-lg border-2 border-white"
        :class="chipClass"
      >
        <EcxSpinner v-if="isPreparing" size="xs" aria-hidden="true" />
        <WarningTriangleIcon v-else-if="isError" class="w-5 h-5" aria-hidden="true" />
        <CheckFilledIcon v-else class="w-5 h-5" aria-hidden="true" />
      </div>

      <p class="flex-1 font-mont text-xl font-semibold leading-6 text-royal-950">
        {{ title }}
      </p>

      <button
        type="button"
        class="shrink-0 p-2 rounded-lg hover:bg-grey-50 transition-colors text-grey-800"
        :aria-label="closeLabel"
        @click="emit('close')"
      >
        <CloseSmallIcon class="w-5 h-5" aria-hidden="true" />
      </button>
    </div>

    <p class="w-full text-sm leading-relaxed tracking-tooltip text-grey-600 whitespace-pre-line">
      {{ description }}
    </p>

    <div v-if="isPreparing" class="flex flex-col gap-1 w-full">
      <div class="h-2 w-full rounded-sm bg-grey-100 overflow-hidden">
        <div
          class="h-full rounded-sm bg-gradient-to-r from-royal-300 to-grey-100"
          :style="{ width: `${progress ?? 0}%` }"
        />
      </div>
      <div class="flex items-center justify-between text-tooltip-title text-grey-600">
        <span>{{ progress ?? 0 }}%</span>
        <span>{{ remainingLabel }}</span>
      </div>
    </div>

    <div
      v-else
      class="flex items-center gap-2"
      :class="status === 'finalized' ? 'justify-between' : 'justify-start'"
    >
      <button
        type="button"
        class="px-3 py-1.5 bg-white border border-grey-100 rounded-md text-xs text-grey-950 hover:bg-grey-50 transition-colors"
        @click="emit('close')"
      >
        {{ closeLabel }}
      </button>

      <button
        v-if="status === 'finalized'"
        type="button"
        class="px-3 py-1.5 bg-royal-500 rounded-md text-xs text-white hover:bg-royal-600 transition-colors"
        @click="emit('download')"
      >
        {{ downloadLabel }}
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'

import { EcxSpinner } from '@ecx/ui'
import { CheckFilledIcon, WarningTriangleIcon, CloseSmallIcon } from '@ecx/ui/icons'

export type BulkExportToastCardStatus = 'preparing' | 'finalized' | 'downloaded' | 'error'

const props = defineProps<{
  status: BulkExportToastCardStatus
  title: string
  description: string
  closeLabel: string
  downloadLabel?: string
  progress?: number
  remainingLabel?: string
}>()

const emit = defineEmits<{
  (e: 'close'): void
  (e: 'download'): void
}>()

const isPreparing = computed(() => props.status === 'preparing')
const isError = computed(() => props.status === 'error')

const chipClass = computed(() => {
  if (props.status === 'preparing') return 'bg-royal-25 ring-1 ring-royal-100 text-royal-500'
  if (props.status === 'error') return 'bg-red-25 ring-1 ring-red-300 text-red-500'
  return 'bg-green-25 ring-1 ring-green-300 text-green-600'
})
</script>
